<template>
  <div
    v-if="loading || skeleton"
    class="create-alert"
    :class="{ 'create-alert--inline d-flex': inline }"
    data-cy-create-alert
  >
    <p v-if="!inline" class="create-alert__title text-secondary">
      <strong class="skeleton d-inline-block" style="padding: 0 60px">
        &nbsp;
      </strong>
    </p>

    <p
      v-if="text && !inline"
      class="create-alert__desc text-small"
      style="overflow: hidden"
    >
      <span
        class="skeleton d-inline-block"
        style="padding: 0 200px; max-width: 100%"
        >&nbsp;</span
      >
    </p>

    <div
      class="skeleton"
      style="width: 280px; height: var(--input-height)"
    ></div>
    <primary-button text="" aspect="light" skeleton style="width: 120px" />
  </div>
  <div
    v-else-if="!isSubscriptionsLoading"
    class="create-alert"
    :class="{ 'create-alert--inline': inline }"
    data-cy-create-alert
  >
    <p
      v-if="!inline"
      class="create-alert__title h5 text-color-tertiary mb-0-6"
      :class="{ 'text-secondary': origin !== ORIGIN.PRODUCT }"
    >
      <strong v-if="date">
        {{
          $t('product.available-from', {
            weekday: $d(date, 'weekday'),
            'month-day': $d(date, 'month-day'),
          })
        }}
      </strong>

      <strong v-else>
        {{ title ? title : $t('product.back-one-day') }}
      </strong>
    </p>

    <p v-if="text" class="create-alert__desc text-small" v-html="text" />

    <form
      :id="isSubscribed ? `${id}-bis_unsubscribe` : `${id}-bis_subscribe`"
      :data-origin="origin"
      novalidate
      @submit="onSubmit($event)"
    >
      <form-input
        :id="`${id}-bis-email`"
        ref="inputEmail"
        v-model="email"
        type="email"
        :label="$t('input.email.label')"
        :placeholder="$t('create_alert.email.placeholder')"
        required
        first-submit
        :disabled="editEmailDisabled"
      />

      <div>
        <primary-button
          :text="
            isSubscribed
              ? $t('action.unsubscribe')
              : $t('create_alert.subscribe')
          "
          type="submit"
          align="center"
          :aspect="inline ? 'dark' : buttonAspect"
          :size="buttonSize"
          :loading="buttonLoading"
        />
      </div>
      <transition name="appear-top">
        <p
          v-if="subscribeError"
          class="text-color-error text-small mt-0-5 mb-0"
        >
          {{ $t('product.alert.error') }}
        </p>
      </transition>
    </form>

    <primary-button
      v-if="withClose"
      type="button"
      :text="$t('action.close')"
      aspect="transparent"
      text-visually-hidden
      with-arrow
      arrow-position="left"
      arrow-is-icon
      icon="cross"
      class="close"
      @click="$emit('close')"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

import FormInput from '@/components/form/Input'
import PrimaryButton from '@/components/buttons/Primary'

import services from '@/services'

import { ORIGIN } from '@/const'
import { EVENTS } from '@/const/events'

export default {
  name: 'ProductCreateAlert',
  components: {
    FormInput,
    PrimaryButton,
  },
  props: {
    id: { type: String, default: 'alert' },
    /**
     * Le produit auquel est associé l'inscription au BIS
     */
    product: { type: Object, required: true },
    /**
     * Affiche une date informative de retour en précommande sinon affiche "de retour un jour"
     */
    date: { type: [Date, Boolean], default: false },
    /**
     * Affiche un titre lorsqu'aucune date n'est renseignée
     */
    title: { type: String, default: '' },
    /**
     * Affiche un texte explicatif
     */
    text: {
      type: String,
      default: null,
    },
    buttonSize: {
      type: String,
      default: 'primary',
    },
    buttonAspect: {
      type: String,
      default: 'light',
    },
    /**
     * Permet de savoir d'ou vient l'inscription, cette donnée est envoyée à Klavyo
     */
    origin: {
      type: String,
      required: true,
      validator: (value) => Object.values(ORIGIN).includes(value),
    },
    /**
     * Permet d'ajouter une croix pour fermer le composant
     */
    withClose: {
      type: Boolean,
      default: false,
    },
    /**
     * Le composant s'affiche sur une ligne
     */
    inline: {
      type: Boolean,
      default: false,
    },
    /**
     * Le composant est affiché en squelette de chargement
     */
    skeleton: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      email: '',
      error: false,
      isSubscribed: false,
      subscribeError: false,
      loading: true,
      buttonLoading: false,
      ORIGIN,
    }
  },
  computed: {
    ...mapGetters({
      connectedEmail: 'subscriptions/connectedEmail',
      account: 'subscriptions/account',
      isSubscriptionsLoading: 'subscriptions/isLoading',
      authenticated: 'auth/authenticated',
    }),
    editEmailDisabled() {
      if (this.isSubscribed) {
        return true
      }

      return this.authenticated && this.connectedEmail !== ''
    },
  },
  watch: {
    isSubscriptionsLoading() {
      this.isSubscribed = this.account.isSubscribed(this.product.productSlug)
    },
  },
  beforeDestroy() {
    this.$nuxt.$off(EVENTS.USER_STATE_LOADED)
  },
  mounted() {
    this.$nuxt.$on(EVENTS.USER_STATE_LOADED, (_) => {
      this.initData()
    })

    this.initData()
  },
  methods: {
    ...mapActions({
      subscribe: 'subscriptions/subscribe',
      unsubscribe: 'subscriptions/unsubscribe',
      setGuestEmail: 'subscriptions/setGuestEmail',
    }),
    initData() {
      if (this.connectedEmail && this.connectedEmail !== '') {
        this.isSubscribed = this.account.isSubscribed(this.product.productSlug)
        this.email = this.connectedEmail
      }

      this.loading = false
    },
    validate() {
      if (!this.$refs.inputEmail.isValid) {
        return false
      }

      return true
    },
    async onSubmit(ev) {
      ev.preventDefault()
      if (this.validate()) {
        const product = {
          ...this.product,
          productImage: '',
          location: this.origin,
        }

        this.buttonLoading = true

        if (
          ((this.connectedEmail && this.connectedEmail === '') ||
            this.connectedEmail !== this.email) &&
          this.account.email !== this.email
        ) {
          await this.setGuestEmail(this.email)
        }

        if (this.isSubscribed) {
          return this.unsubscribe(product)
            .then(() => {
              this.isSubscribed = this.account.isSubscribed(
                this.product.productSlug
              )

              this.$track.bisUnSubscribe(this.product.productSlug, this.origin)

              this.showToaster('deactivated')

              /**
               * @event has-unsubscribe
               * Envoyé après qu'un utilisateur se soit désinscrit d'une alert
               */
              this.$emit('has-unsubscribe')
            })
            .catch((err) => {
              this.$logError.captureException(err)
              this.subscribeError = true
            })
            .finally((_) => {
              this.buttonLoading = false
            })
        }

        return this.subscribe(product)
          .then(() => {
            this.isSubscribed = this.account.isSubscribed(
              this.product.productSlug
            )

            this.$nuxt.$emit(EVENTS.ASK_NOTIFICATION, this.origin)
            this.$track.bisSubscribe(
              this.account.email,
              this.product.productSlug,
              this.origin
            )

            if (this.connectedEmail === '') {
              this.showModal()
            } else {
              this.buttonLoading = false
              this.showToaster('activated')
            }

            /**
             * @event has-subscribe
             * Envoyé après qu'un utilisateur se soit désinscrit d'une alert
             */
            this.$emit('has-subscribe')
          })
          .catch((err) => {
            this.$logError.captureException(err)
            this.subscribeError = true
          })
          .finally((_) => {
            this.buttonLoading = false
          })
      } else {
        document.getElementById(`${this.id}-bis-email`).focus()
      }

      return false
    },
    showModal() {
      services.accountManagerService
        .getCustomerStatus(this.email)
        .then((res) => {
          this.$nuxt.$emit(EVENTS.SHOW_FORCE_CONNECT, {
            email: this.email,
            status: res.data.data.status,
          })
          this.buttonLoading = false
        })
    },
    showToaster(type) {
      this.$toasted.clear()
      this.$toasted.show(
        `<div>
            ${this.$t(`product.alert.${type}`, {
              produit: this.product.productName,
              email: this.email,
            })}
          </div>
          `,
        {
          position: 'bottom-center',
          duration: 5000,
          keepOnHover: true,
          action: {
            text: '',
            icon: 'close',
            class: 'toasted__close',
            onClick: (_, toastObject) => {
              toastObject.goAway(0)
            },
          },
        }
      )
    },
  },
}
</script>

<style lang="scss">
.create-alert {
  .close {
    position: absolute;
    top: 0;
    right: 0;
    color: var(--tertiary-color);
    height: auto;
    padding: calc(var(--spacing));

    @include mq($from: tablet) {
      padding: calc(var(--spacing) * 1.4);
    }

    .icon {
      width: var(--icon-size);
      height: var(--icon-size);

      @include mq($until: tablet) {
        --icon-size: #{rem(15px)};
      }

      @include mq($from: tablet) {
        --icon-size: #{rem(20px)};
      }
    }
  }

  &--inline {
    form {
      display: flex;
    }

    .input {
      margin-bottom: 0;

      @include mq($until: tablet) {
        width: auto;
        flex: 1 1 auto;
      }
    }

    .input__container {
      border: 0;
    }

    .input__line {
      bottom: 0;
    }

    .action {
      box-shadow: none;
      white-space: nowrap;

      @include on-hover-and-focus {
        box-shadow: none;
      }
    }
  }
}
</style>
